import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  Circle,
  Clock,
  Coffee,
  Loader2,
  LogOut,
  MapPin,
  Play,
} from 'lucide-react';
import ActiveJobTimerBeforeActionModal, {
  type BeforeActionType,
} from '../ActiveJobTimerBeforeActionModal';
import {
  useActiveWorkers,
  calculateLiveClockEarnings,
  getWorkerHourlyRate,
  locationStatus,
  type ClockedInWorker,
  type WorkerActiveJob,
  type WorkerLocationSnapshot,
} from '../../hooks/useActiveWorkers';
import TeamMapModal from './TeamMapModal';
import { useSupabase } from '../../contexts/SupabaseContext';
import {
  clockOutWorker,
  endWorkerBreak,
  fetchActiveJobTimers,
  isWorkerOnBreak,
  startWorkerBreak,
  stopActiveJobTimers,
  type ActiveJobTimerEntry,
} from '../../lib/activeWorkerActions';
import { formatCurrency } from '../../lib/utils';

const panelFont =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif";

function getResourceMeta(entry: ClockedInWorker) {
  const data = entry.resources || entry.resource;
  const firstName = data?.first_name ?? '';
  const lastName = data?.last_name ?? '';
  const name = `${firstName} ${lastName}`.trim() || 'Unknown';
  const initials = name
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return { name, initials, avatarUrl: data?.avatar_url ?? null };
}

function formatElapsed(clockIn: string, now: Date) {
  const elapsedMs = now.getTime() - new Date(clockIn).getTime();
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatLastSeen(at: string | null | undefined, now: Date) {
  if (!at) return null;
  const minutes = Math.max(0, Math.round((now.getTime() - new Date(at).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
}

function activityLabel(activity: string | null | undefined) {
  if (!activity || activity === 'unknown') return null;
  const map: Record<string, { emoji: string; label: string }> = {
    still: { emoji: '🧍', label: 'Still' },
    walking: { emoji: '🚶', label: 'Walking' },
    on_foot: { emoji: '🚶', label: 'Walking' },
    running: { emoji: '🏃', label: 'Running' },
    on_bicycle: { emoji: '🚴', label: 'Cycling' },
    in_vehicle: { emoji: '🚗', label: 'Driving' },
  };
  return map[activity] ?? null;
}

interface WorkerCardProps {
  entry: ClockedInWorker;
  job?: WorkerActiveJob;
  location?: WorkerLocationSnapshot;
  currentTime: Date;
  onClick?: () => void;
  compact?: boolean;
  onStartBreak: () => void;
  onEndBreak: () => void;
  onClockOut: () => void;
  actionBusy?: boolean;
}

interface WorkerActionsMenuProps {
  entry: ClockedInWorker;
  onStartBreak: () => void;
  onEndBreak: () => void;
  onClockOut: () => void;
  disabled?: boolean;
}

function WorkerActionsMenu({
  entry,
  onStartBreak,
  onEndBreak,
  onClockOut,
  disabled = false,
}: WorkerActionsMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const onBreak = isWorkerOnBreak(entry);

  const openMenu = () => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const width = Math.min(window.innerWidth - 16, 220);
    const estimatedHeight = 120;
    let left = rect.right - width;
    if (left < 8) left = 8;
    let top = rect.bottom + 4;
    // Flip above the trigger when there isn't room below (e.g. last worker in a list).
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - 4);
    }
    setMenuPos({ top, left, width });
    setMenuOpen(true);
  };

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setMenuOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    // The menu is portaled with fixed positioning; close on scroll/resize so it
    // can't drift away from its trigger.
    const handleReposition = () => setMenuOpen(false);

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [menuOpen]);

  return (
    <div ref={rootRef} className="relative shrink-0 self-center">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          if (menuOpen) {
            setMenuOpen(false);
          } else {
            openMenu();
          }
        }}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 active:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 sm:h-9 sm:w-9 sm:rounded-xl"
        aria-label="Worker actions"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
          strokeWidth={2.25}
        />
      </button>

      {menuOpen && menuPos
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-[100000] rounded-xl border border-gray-200/90 bg-white py-1 shadow-lg"
              style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
              onClick={(event) => event.stopPropagation()}
            >
          {!onBreak ? (
            <button
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={() => {
                setMenuOpen(false);
                onStartBreak();
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] font-medium text-gray-900 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Coffee className="h-4 w-4 shrink-0 text-amber-600" strokeWidth={2.25} />
              Start break
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={() => {
                setMenuOpen(false);
                onEndBreak();
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] font-medium text-gray-900 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Play className="h-4 w-4 shrink-0 text-green-600" strokeWidth={2.25} />
              End break
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            disabled={disabled}
            onClick={() => {
              setMenuOpen(false);
              onClockOut();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[13px] font-medium text-red-700 transition-colors hover:bg-red-50 active:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <LogOut className="h-4 w-4 shrink-0 text-red-600" strokeWidth={2.25} />
            Clock out
          </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

function WorkerCard({
  entry,
  job,
  location,
  currentTime,
  onClick,
  compact = false,
  onStartBreak,
  onEndBreak,
  onClockOut,
  actionBusy = false,
}: WorkerCardProps) {
  const { name, initials, avatarUrl } = getResourceMeta(entry);
  const act = activityLabel(location?.activity);
  const hourlyRate = getWorkerHourlyRate(entry);
  const showEarnings = hourlyRate > 0;
  const liveEarnings = showEarnings
    ? calculateLiveClockEarnings(entry.clock_in, hourlyRate, currentTime, {
        breakStart: entry.break_start,
        breakEnd: entry.break_end,
      })
    : 0;
  const speedMph =
    location?.speed != null && location.speed > 0
      ? (location.speed * 2.237).toFixed(0)
      : null;

  const statsParts: string[] = [
    formatElapsed(entry.clock_in, currentTime),
    location?.odometer != null && location.odometer > 0
      ? `${(location.odometer / 1000).toFixed(1)} km`
      : null,
    act ? `${act.emoji} ${act.label}${speedMph ? ` · ${speedMph} mph` : ''}` : null,
  ].filter(Boolean) as string[];

  const statsLine = statsParts.join(' · ');

  // Location sharing: a pulsing blue dot when the phone is reporting, grey
  // when it has gone quiet, nothing when it never sent a fix this shift.
  const sharing = locationStatus(location, currentTime);
  const sharingLine =
    sharing === 'live'
      ? 'Sharing location'
      : sharing === 'stale'
        ? `Last seen ${formatLastSeen(location?.at, currentTime)}`
        : null;
  const sharingTitle =
    sharing === 'live'
      ? 'Sharing location. Tap to see on the map.'
      : sharing === 'stale'
        ? `Location last reported ${formatLastSeen(location?.at, currentTime)}. Tap to see on the map.`
        : 'Not sharing location';

  const avatar = (
    <div className="relative shrink-0" title={sharingTitle}>
      {sharing === 'live' ? (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-full border-2 border-blue-500/50 bg-blue-500/10"
          style={{ animation: 'cc-worker-live-ring 2s ease-in-out infinite' }}
        />
      ) : null}
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className={`relative h-9 w-9 rounded-full object-cover shadow-sm sm:h-10 sm:w-10 ${
            sharing === 'live' ? 'ring-2 ring-blue-500' : 'ring-[1.5px] ring-white sm:ring-2'
          }`}
        />
      ) : (
        <div
          className={`relative flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm sm:h-10 sm:w-10 ${
            sharing === 'live' ? 'ring-2 ring-blue-500' : 'ring-[1.5px] ring-white sm:ring-2'
          }`}
        >
          <span className="text-[11px] font-semibold text-white sm:text-xs">{initials}</span>
        </div>
      )}
      <div className="absolute -bottom-px -right-px rounded-full bg-white p-px sm:-bottom-0.5 sm:-right-0.5 sm:p-0.5">
        {sharing === 'live' ? (
          <MapPin className="h-2.5 w-2.5 fill-blue-500 text-blue-600 sm:h-3 sm:w-3" strokeWidth={2.5} />
        ) : sharing === 'stale' ? (
          <MapPin className="h-2.5 w-2.5 fill-gray-300 text-gray-400 sm:h-3 sm:w-3" strokeWidth={2.5} />
        ) : (
          <Circle className="h-2.5 w-2.5 fill-green-500 text-green-500 sm:h-3 sm:w-3" />
        )}
      </div>
    </div>
  );

  const onBreak = isWorkerOnBreak(entry);

  const cardBody = (
    <>
      {avatar}

      {/* Mobile: name + earnings on one row, stats below */}
      <div className="min-w-0 flex-1 sm:hidden">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <p
            className="truncate text-[14px] font-semibold text-gray-900 leading-tight"
            style={{ letterSpacing: '-0.01em' }}
          >
            {name}
          </p>
          {showEarnings ? (
            <span className="shrink-0 text-[12px] font-semibold text-emerald-700 tabular-nums">
              {formatCurrency(liveEarnings)}
            </span>
          ) : null}
        </div>
        {onBreak ? (
          <p className="mt-0.5 text-[11px] font-medium text-amber-700 leading-tight">On break</p>
        ) : null}
        {job ? (
          <p className="mt-0.5 truncate text-[11px] font-medium text-blue-600 leading-tight">
            #{job.booking_number} {job.booking_title || ''}
          </p>
        ) : null}
        {statsLine ? (
          <p className="truncate text-[11px] text-gray-500 leading-tight mt-0.5">{statsLine}</p>
        ) : null}
        {sharingLine ? (
          <p
            className={`mt-0.5 truncate text-[11px] font-medium leading-tight ${
              sharing === 'live' ? 'text-blue-600' : 'text-gray-400'
            }`}
          >
            {sharingLine}
          </p>
        ) : null}
      </div>

      {/* Tablet+ sidebar / horizontal */}
      <div className="min-w-0 flex-1 hidden sm:block">
        <p
          className="truncate text-[14px] font-semibold text-gray-900"
          style={{ letterSpacing: '-0.01em' }}
        >
          {name}
        </p>
        {onBreak ? (
          <p className="mt-0.5 text-[12px] font-medium text-amber-700">On break</p>
        ) : null}
        {job ? (
          <p className="mt-0.5 truncate text-[12px] font-medium text-blue-600">
            #{job.booking_number} {job.booking_title || ''}
          </p>
        ) : null}
        <div className="mt-1 space-y-0.5">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-gray-500">
            <span className="tabular-nums">{formatElapsed(entry.clock_in, currentTime)}</span>
            {location?.odometer != null && location.odometer > 0 ? (
              <span className="tabular-nums">· {(location.odometer / 1000).toFixed(1)} km</span>
            ) : null}
            {act ? (
              <span>
                {act.emoji} {act.label}
                {speedMph ? ` · ${speedMph} mph` : ''}
              </span>
            ) : null}
          </div>
          {showEarnings ? (
            <p className="text-[12px] font-semibold text-emerald-700 tabular-nums">
              {formatCurrency(liveEarnings)}
            </p>
          ) : null}
          {sharingLine ? (
            <p
              className={`truncate text-[12px] font-medium ${
                sharing === 'live' ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              {sharingLine}
            </p>
          ) : null}
        </div>
      </div>
    </>
  );

  return (
    <div
      className={`relative flex items-center gap-1 rounded-xl px-1 py-1 sm:gap-1.5 sm:rounded-2xl sm:px-1.5 sm:py-1.5 ${
        compact
          ? 'w-full min-w-0 shrink-0'
          : 'shrink-0 min-w-[min(100%,240px)] max-w-[300px]'
      }`}
      data-cc-card
      style={{ fontFamily: panelFont }}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          disabled={actionBusy}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-1.5 py-1 text-left transition-all hover:bg-gray-50 active:bg-gray-100 active:scale-[0.99] touch-manipulation cursor-pointer disabled:opacity-70 sm:gap-3 sm:rounded-xl sm:px-2 sm:py-1.5"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {cardBody}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2.5 px-1.5 py-1 sm:gap-3 sm:px-2 sm:py-1.5">
          {cardBody}
        </div>
      )}

      <WorkerActionsMenu
        entry={entry}
        onStartBreak={onStartBreak}
        onEndBreak={onEndBreak}
        onClockOut={onClockOut}
        disabled={actionBusy}
      />
    </div>
  );
}

interface ActiveWorkersPanelProps {
  onWorkerClick?: (resourceId: string) => void;
  /** Tapping a worker opens the live team map modal focused on them (when onWorkerClick is not given). */
  openMapOnClick?: boolean;
  /** horizontal = one scrolling row; sidebar = stacked column beside schedule */
  layout?: 'horizontal' | 'sidebar';
  /** Hide the whole strip when nobody is clocked in. */
  hideWhenEmpty?: boolean;
}

/** Active workers for the control centre calendar panel. */
export default function ActiveWorkersPanel({
  onWorkerClick,
  openMapOnClick = false,
  layout = 'horizontal',
  hideWhenEmpty = false,
}: ActiveWorkersPanelProps) {
  const supabase = useSupabase();
  const { workers, activeJobs, locations, canView, loading, currentTime, refresh } =
    useActiveWorkers();

  const [mapResourceId, setMapResourceId] = useState<string | null>(null);
  const [busyEntryId, setBusyEntryId] = useState<string | null>(null);
  const [timerModalOpen, setTimerModalOpen] = useState(false);
  const [timerModalLoading, setTimerModalLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<BeforeActionType | null>(null);
  const [pendingEntry, setPendingEntry] = useState<ClockedInWorker | null>(null);
  const [pendingTimerEntries, setPendingTimerEntries] = useState<ActiveJobTimerEntry[]>([]);

  const getWorkerName = (entry: ClockedInWorker) => {
    const data = entry.resources || entry.resource;
    const name = `${data?.first_name ?? ''} ${data?.last_name ?? ''}`.trim();
    return name || 'This worker';
  };

  const runClockOut = async (entry: ClockedInWorker) => {
    if (!supabase) return;
    await clockOutWorker(supabase, {
      payrollEntryId: entry.id,
      resourceId: entry.resource_id,
      clockIn: entry.clock_in,
      breakStart: entry.break_start,
      breakEnd: entry.break_end,
    });
    await refresh();
  };

  const runStartBreak = async (entry: ClockedInWorker) => {
    if (!supabase) return;
    await startWorkerBreak(supabase, entry.id, entry.resource_id);
    await refresh();
  };

  const runEndBreak = async (entry: ClockedInWorker) => {
    if (!supabase) return;
    await endWorkerBreak(supabase, entry.id, entry.resource_id);
    await refresh();
  };

  const handleWorkerAction = async (
    entry: ClockedInWorker,
    action: BeforeActionType | 'end_break'
  ) => {
    if (!supabase || busyEntryId) return;

    if (action === 'end_break') {
      setBusyEntryId(entry.id);
      try {
        await runEndBreak(entry);
      } catch (error) {
        console.error('Error ending worker break:', error);
        alert(`Failed to end break for ${getWorkerName(entry)}. Please try again.`);
      } finally {
        setBusyEntryId(null);
      }
      return;
    }

    setBusyEntryId(entry.id);
    try {
      const timerEntries = await fetchActiveJobTimers(supabase, entry.resource_id);
      if (timerEntries.length > 0) {
        setPendingEntry(entry);
        setPendingAction(action);
        setPendingTimerEntries(timerEntries);
        setTimerModalOpen(true);
        return;
      }

      if (action === 'clock_out') {
        await runClockOut(entry);
      } else {
        await runStartBreak(entry);
      }
    } catch (error) {
      console.error('Error updating worker timeclock:', error);
      alert(`Failed to update ${getWorkerName(entry)}. Please try again.`);
    } finally {
      setBusyEntryId(null);
    }
  };

  const handleStopTimersAndProceed = async () => {
    if (!supabase || !pendingEntry || !pendingAction) return;

    setTimerModalLoading(true);
    try {
      await stopActiveJobTimers(supabase, pendingEntry.resource_id, pendingTimerEntries);
      if (pendingAction === 'clock_out') {
        await runClockOut(pendingEntry);
      } else {
        await runStartBreak(pendingEntry);
      }
      setTimerModalOpen(false);
      setPendingEntry(null);
      setPendingAction(null);
      setPendingTimerEntries([]);
    } catch (error) {
      console.error('Error stopping job timers:', error);
      alert(`Failed to update ${getWorkerName(pendingEntry)}. Please try again.`);
    } finally {
      setTimerModalLoading(false);
      setBusyEntryId(null);
    }
  };

  const closeTimerModal = () => {
    if (timerModalLoading) return;
    setTimerModalOpen(false);
    setPendingEntry(null);
    setPendingAction(null);
    setPendingTimerEntries([]);
    setBusyEntryId(null);
  };

  if (!canView) return null;
  if (hideWhenEmpty && (loading || workers.length === 0)) return null;

  const jobByResource = new Map(activeJobs.map((j) => [j.resource_id, j]));
  const isSidebar = layout === 'sidebar';

  return (
    <div
      className={`w-full rounded-lg overflow-hidden ${
        isSidebar ? 'h-full min-h-0 flex flex-col max-lg:h-auto max-lg:flex-none' : ''
      }`}
      data-cc-inset
      style={{ fontFamily: panelFont }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 min-h-[36px] sm:px-3 sm:py-2 sm:min-h-[36px]"
        data-cc-inset-header
      >
        <Clock className="h-4 w-4 shrink-0 text-green-600" strokeWidth={2.25} />
        <h3
          className="text-[14px] font-semibold text-gray-900 leading-none sm:text-[14px]"
          style={{ letterSpacing: '-0.01em' }}
        >
          Active Workers
        </h3>
        {!loading && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-600 tabular-nums leading-none">
            {workers.length}
          </span>
        )}
        {loading && <Loader2 className="ml-auto h-3.5 w-3.5 shrink-0 animate-spin text-gray-400" />}
      </div>

      {!loading && (
      <div
        className={`p-2 sm:p-2 ${
          isSidebar ? 'flex flex-col max-lg:flex-none lg:flex-1 lg:min-h-0' : ''
        }`}
      >
        {workers.length === 0 ? (
          <p className="py-2 text-center text-[13px] leading-snug text-gray-500 sm:py-2.5 sm:text-[13px]">
            No one clocked in
          </p>
        ) : (
          <div
            className={
              isSidebar
                ? 'flex flex-col gap-1.5 overflow-y-auto ios-momentum-scroll max-lg:max-h-[12.5rem] -mx-0.5 px-0.5 sm:gap-2 lg:max-h-none lg:min-h-0 lg:flex-1'
                : 'flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden pb-0.5 -mx-0.5 px-0.5 ios-momentum-scroll overscroll-x-contain sm:gap-2.5'
            }
          >
            {workers.map((entry) => (
              <WorkerCard
                key={entry.id}
                entry={entry}
                job={jobByResource.get(entry.resource_id)}
                location={locations.get(entry.resource_id)}
                currentTime={currentTime}
                compact={isSidebar}
                actionBusy={busyEntryId === entry.id}
                onStartBreak={() => handleWorkerAction(entry, 'break')}
                onEndBreak={() => handleWorkerAction(entry, 'end_break')}
                onClockOut={() => handleWorkerAction(entry, 'clock_out')}
                onClick={
                  onWorkerClick
                    ? () => onWorkerClick(entry.resource_id)
                    : openMapOnClick
                      ? () => setMapResourceId(entry.resource_id)
                      : undefined
                }
              />
            ))}
          </div>
        )}
      </div>
      )}

      <style>{`
        @keyframes cc-worker-live-ring {
          0% { transform: scale(0.9); opacity: 0.9; }
          50% { transform: scale(1.25); opacity: 0.2; }
          100% { transform: scale(0.9); opacity: 0.9; }
        }
      `}</style>

      {openMapOnClick && mapResourceId !== null ? (
        <TeamMapModal
          isOpen
          focusResourceId={mapResourceId}
          onClose={() => setMapResourceId(null)}
        />
      ) : null}

      <ActiveJobTimerBeforeActionModal
        isOpen={timerModalOpen}
        onClose={closeTimerModal}
        actionType={pendingAction ?? 'clock_out'}
        activeTimerCount={pendingTimerEntries.length}
        onStopAndProceed={handleStopTimersAndProceed}
        loading={timerModalLoading}
      />
    </div>
  );
}
